import Config from '@config';
import { CACHE_EXPIRATION_TIME } from '@gasbi/shared/common/features/constants';
import {
  FiltersGrid,
  Pagination,
  infiniteQueryOptions,
  normalizeInfiniteQueryData,
} from '@gasbi/shared/index';
import { createApi } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

import { PowerRateTariff, PowerRateTariffRequestBody } from './types';

export const powerRateTariffsApi = createApi({
  reducerPath: 'powerRateTariffsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/power-rate-tariffs`,
  }),
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  tagTypes: ['PowerRateTariff', 'PowerRateTariffs'],
  endpoints: builder => ({
    getPowerRateTariffI: builder.infiniteQuery<
      PowerRateTariff[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['PowerRateTariff', 'PowerRateTariffs'],
    }),
    getPowerRateTariffById: builder.query<PowerRateTariff, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['PowerRateTariff'],
    }),
    createPowerRateTariff: builder.mutation<string, PowerRateTariffRequestBody>(
      {
        query: data => ({
          url: '/',
          method: 'POST',
          data,
        }),
        invalidatesTags: () => ['PowerRateTariffs'],
      },
    ),
    updatePowerRateTariff: builder.mutation<void, PowerRateTariffRequestBody>({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_, __, data) => [
        'PowerRateTariffs',
        { type: 'PowerRateTariff', id: data.id },
      ],
    }),
  }),
});

export const {
  useGetPowerRateTariffByIdQuery,
  useCreatePowerRateTariffMutation,
  useUpdatePowerRateTariffMutation,
} = powerRateTariffsApi;

export const useGetPowerRateTariffIInfiniteQuery =
  normalizeInfiniteQueryData<PowerRateTariff>(
    powerRateTariffsApi.useGetPowerRateTariffIInfiniteQuery,
  );
